import * as React from "react";
import { Ref, useCallback, useMemo } from "react";
import { styled } from "@mui/material";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import { CHROME_ELEMENTS_FONT_MD } from "fe-web-components/styles/typography";
import { usePatientCase } from "../usePatientCase";
import { MeasurementUnits } from "fe-common/graphql";
import { SectionTitle } from "./SectionMainTitle";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { useScreenSize } from "../util/useScreenSize";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { useRouteHelper } from "../useRouteHelper";

//region [[ Styles ]]
const AgePickerView = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  marginLeft: "75px",
  justifyContent: "flex-start",
  [theme.breakpoints.down(MobileView)]: {
    width: "100%",
    marginTop: "22px",
    marginLeft: "unset",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const AgeTextField = styled(TextField)({
  "& fieldset": {
    border: "none",
  },
});

const useStyles = makeStyles(() => ({
  inputMobile: {
    fontSize: "58px",
    fontWeight: 400,
    width: "150px",
    textAlign: "center",
    border: "none",
  },
  inputDesktop: {
    fontSize: "40px",
    fontWeight: 400,
    width: "100px",
    height: "34px",
    textAlign: "center",
    border: "none",
  },
}));

const AgeComponent = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "start",
  fontFamily: `${CHROME_ELEMENTS_FONT_MD}`,
  marginTop: "17px",
  [theme.breakpoints.down(MobileView)]: {
    width: "100%",
    justifyContent: "center",
    paddingLeft: theme.spacing(10),
    paddingRight: theme.spacing(10),
  },
}));

const StyledSectionTitle = styled(SectionTitle)(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    textAlign: "left",
    marginBottom: "80px",
  },
  textAlign: "center",
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface AgePickerProps {
  className?: string;
  inputRef?: Ref<any>;
  title?: string;
  onBlur?: () => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

const initialAge = {
  low: null,
  high: null,
  units: MeasurementUnits.Years,
};

export const AgePicker = ({ inputRef, onBlur, ...props }: AgePickerProps) => {
  const classes = useStyles();
  const { patientCase, handleSetAge } = usePatientCase();
  const { isMobile } = useScreenSize();
  const { inWizard } = useDiagnosticNavigation();

  const { getNextRoute, navigateTo } = useRouteHelper();

  const age = useMemo(() => patientCase?.age || initialAge, [patientCase]);

  const changeAgeNumber = useCallback(
    (event) => {
      let newAge =
        event.target.value === "" ? null : parseInt(event.target.value);

      if (newAge === null || (!isNaN(newAge) && newAge > 0 && newAge < 150)) {
        // TODO: let the age picker select something other than years
        //  - remove this hard codded line
        handleSetAge(newAge, MeasurementUnits.Years);
      }
    },
    [handleSetAge],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && inWizard && age.high) {
        navigateTo({ route: getNextRoute().route });
      }
    },
    [navigateTo, getNextRoute, inWizard, age.high],
  );

  return (
    <AgePickerView className={props.className}>
      <StyledSectionTitle className={"section-title"}>
        {props.title || "Enter Age"}
      </StyledSectionTitle>
      <AgeComponent>
        <AgeTextField
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          inputRef={inputRef}
          variant="outlined"
          type="number"
          autoFocus={isMobile}
          InputLabelProps={{
            shrink: true,
          }}
          style={{
            backgroundColor: "white",
          }}
          InputProps={{
            classes: {
              input: isMobile ? classes.inputMobile : classes.inputDesktop,
            },
          }}
          value={age?.low || ""}
          onChange={changeAgeNumber}
        />
      </AgeComponent>
    </AgePickerView>
  );
};
